import LineItemFormField from '@/Enums/CampaignManagement/LineItemFormField.enum';
import Channel from '@/Enums/Channel.enum';
import Color from '@/Enums/Color.enum';
import InventoryType from '@/Enums/InventoryType.enum';
import type { IProduct } from '@/Interfaces/Product.interface';
import LineItemDeviceService from '@/Services/CampaignManagement/Device.service';
import SearchIcon from '@/components/Icons/SearchIcon';
import SupportRequestModal from '@/components/Modal/SupportRequestModal';
import ProductDomainList from '@/components/ProductDomainList';
import ProductInventorySearch from '@/components/ProductInventorySearch';
import ProductInventoryTabs from '@/components/ProductInventoryTabs';
import StyledInput from '@/components/TabSelect/styled/StyledInput';
import { NEW_ITEM_ID } from '@/constants/campaignManagement';
import { devicesToControlOptions } from '@/helpers/devices';
import { filterByName, getProductInventoryHint } from '@/helpers/productInventory';
import { sortByLabelOrName } from '@/helpers/sort';
import useInventoryTabs from '@/hooks/useInventoryTabs';
import useListSearch from '@/hooks/useListSearch';
import useProductDomains from '@/hooks/useProductDomains';
import { useAppSelector } from '@/store/hooks';
import { selectCampaignManagementProducts, selectLineItem } from '@/store/selectors/campaignManagement.selectors';
import {
  selectIsPresetsMode,
  selectIsProductSelectionCardHidden,
  selectIsRequestProductButtonHidden,
} from '@/store/selectors/currentWhitelabel.selectors';
import type { LineItemChannel } from '@/types/LineItem/channel.types';
import { Box, Button, FormControlLabel, FormLabel, Switch } from '@mui/material';
import classNames from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import CustomDomainLists from './CustomDomainLists';
import ProductDomainExport from './ProductDomainExport';
import PublisherList from './PublisherList';
import PublisherSection from './PublisherSection';

interface IProps {
  index: number;
}

const ProductAndPublishersSelection = ({ index }: IProps) => {
  const [requestProductsModalOpen, setRequestProductsModalOpen] = useState(false);
  const [customToggle, setCustomToggle] = useState(false);
  const { search, searchTerm, onSearchValueChange } = useListSearch();

  const activeLineItem = useAppSelector(state => selectLineItem(state, index));
  const { products: productsWithPublishers } = useAppSelector(selectCampaignManagementProducts);
  const presetsMode = useAppSelector(selectIsPresetsMode);
  const isRequestProductButtonHidden = useAppSelector(selectIsRequestProductButtonHidden);
  const isProductSelectionCardHidden = useAppSelector(selectIsProductSelectionCardHidden);

  const publishersListRef = useRef<HTMLDivElement>(null);

  const { control, setValue, trigger } = useFormContext();

  const [watchId, watchProduct, watchChannelOption, watchHasCustomDomainLists]: [
    string,
    IProduct | null,
    { label: LineItemChannel } | null,
    boolean | undefined,
  ] = useWatch({
    control,
    name: ['id', LineItemFormField.Product, 'channel', LineItemFormField.HasCustomDomainLists],
  });
  const watchChannel = watchChannelOption?.label;

  const isExportableChannel = watchChannel === Channel.Display || watchChannel === Channel.OLV;
  const isCustomMode = customToggle || !!watchHasCustomDomainLists;
  const showCustomToggle = presetsMode && isExportableChannel;
  const isToggleLocked = !!activeLineItem?.has_custom_domain_lists;

  useEffect(() => {
    if (watchHasCustomDomainLists) {
      setCustomToggle(true);
    }
  }, [watchHasCustomDomainLists]);

  const handleProductSelection = useCallback(
    ({ id, name, rateCardCpm, devices, publishers }: IProduct) =>
      () => {
        if (watchProduct && id !== watchProduct.id) {
          publishersListRef.current?.scrollTo(0, 0);
        }
        setValue(LineItemFormField.Product, { id, name, rateCardCpm }, { shouldDirty: true, shouldValidate: true });
        setValue(
          'devices',
          LineItemDeviceService.filterDevicesByChannel(
            watchChannel,
            devicesToControlOptions(activeLineItem?.product?.id === id ? (activeLineItem?.devices ?? []) : devices),
          ),
          { shouldDirty: true, shouldValidate: true },
        );
        if (presetsMode) {
          setValue('publisherIds', publishers?.map(pub => pub.id) ?? []);
        }
      },
    [setValue, watchProduct, activeLineItem, presetsMode, watchChannel],
  );

  const onRequestProductsOpen = useCallback(() => {
    setRequestProductsModalOpen(true);
  }, []);

  const onRequestProductsClose = useCallback(() => {
    setRequestProductsModalOpen(false);
  }, []);

  const currentChannelProducts = useMemo(() => {
    const products = productsWithPublishers?.[watchChannel]?.products || [];
    return sortByLabelOrName(products) as IProduct[];
  }, [productsWithPublishers, watchChannel]);

  const displayedProducts = useMemo(() => {
    if (!searchTerm.length) return currentChannelProducts;
    return currentChannelProducts.filter(p => p.name.toLowerCase().includes(searchTerm));
  }, [currentChannelProducts, searchTerm]);

  // https://github.com/ribeyemedia/flank/issues/2237
  // allow product selection for `Percent of Media` orgs
  const isProductSelectionDisabled = useMemo(() => watchId !== NEW_ITEM_ID && !presetsMode, [watchId, presetsMode]);

  const selectedProduct = useMemo(() => {
    if (!watchProduct?.id) return null;
    return currentChannelProducts.find(p => p.id === watchProduct.id) ?? null;
  }, [watchProduct?.id, currentChannelProducts]);
  const selectedPublishers = useMemo(() => selectedProduct?.publishers ?? null, [selectedProduct]);
  // Only products from /products/by-channels carry hasDomains, so Direct Audio never resolves a domain list here.
  const hasDomains = !!selectedProduct?.hasDomains;

  const {
    domains,
    isLoading: areDomainsLoading,
    error: domainsError,
  } = useProductDomains(watchProduct?.id && (isExportableChannel || hasDomains) ? watchProduct.id : null);
  const { tabs, activeTab, setActiveTab } = useInventoryTabs({
    productId: watchProduct?.id,
    publishersCount: selectedPublishers?.length ?? 0,
    hasDomains,
    domainsCount: domains.length || selectedProduct?.domainsCount,
  });
  const showDomains = hasDomains && activeTab === InventoryType.Domains;
  const showPublishers = !!selectedPublishers && !showDomains;
  const showProductDomainExport = !!watchProduct?.id && (isExportableChannel || !!domains.length);

  const {
    search: inventorySearch,
    searchTerm: inventorySearchTerm,
    onSearchValueChange: onInventorySearchChange,
    clearSearch: clearInventorySearch,
  } = useListSearch();
  useEffect(() => {
    clearInventorySearch();
  }, [activeTab, watchProduct?.id, clearInventorySearch]);

  const displayedPublishers = useMemo(
    () => filterByName(selectedPublishers ?? [], inventorySearchTerm),
    [selectedPublishers, inventorySearchTerm],
  );
  const displayedDomains = useMemo(() => filterByName(domains, inventorySearchTerm), [domains, inventorySearchTerm]);
  const activeInventoryCount = showDomains ? domains.length : (selectedPublishers?.length ?? 0);
  const displayedInventoryCount = showDomains ? displayedDomains.length : displayedPublishers.length;
  // Presets keep their own search inside PublisherSection, which drives its Select All.
  const showInventorySearch = !!activeInventoryCount && !(showPublishers && presetsMode);

  if (isProductSelectionCardHidden) {
    return null;
  }

  return (
    <div className='flex flex-col mt-2'>
      <div className='flex justify-between items-end'>
        <div className='flex items-center gap-2'>
          <FormLabel
            aria-label={presetsMode ? 'Presets' : 'Product'}
            required={!isProductSelectionDisabled && !isCustomMode}
          >
            {presetsMode ? 'Presets' : 'Product'}
          </FormLabel>
          {showCustomToggle && (
            <FormControlLabel
              control={
                <Switch
                  size='small'
                  checked={isCustomMode}
                  onChange={(_, checked) => {
                    setCustomToggle(checked);
                    setValue(LineItemFormField.HasCustomDomainLists, checked, { shouldDirty: true });
                    trigger([
                      LineItemFormField.CustomAllowDomains,
                      LineItemFormField.CustomBlockDomains,
                      LineItemFormField.Product,
                    ]);
                  }}
                  disabled={isToggleLocked}
                />
              }
              label='Custom'
              sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: '14px', fontFamily: 'Manrope' } }}
            />
          )}
        </div>
        {!isCustomMode && !isRequestProductButtonHidden && (
          <Button sx={{ marginBottom: '8px', py: '4px' }} onClick={onRequestProductsOpen} variant='contained'>
            {presetsMode ? 'Request Presets' : 'Request Products'}
          </Button>
        )}
      </div>
      {isCustomMode ? (
        <CustomDomainLists />
      ) : (
        <div className={classNames('flex flex-col border border-[#E8E8E8] rounded-md')}>
          <div className='w-full flex bg-[#E8E8E8]/40 rounded-t-lg'>
            <p className={`w-1/2 px-4 py-2 border-r font-[Manrope] text-[${Color.Grey}] text-[14px] font-semibold`}>
              {presetsMode ? 'Available Presets' : 'Available Products'}
            </p>
            <ProductInventoryTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} className='w-1/2 px-4' />
          </div>
          <div className='flex h-72'>
            <Box
              className={classNames('flex flex-col w-1/2 overflow-y-auto p-2 border-r', {
                'bg-[#E8E8E8]/40': isProductSelectionDisabled,
              })}
            >
              <div className='flex gap-2 mb-1'>
                <StyledInput
                  fullWidth
                  startAdornment={<SearchIcon className={`text-[${Color.Grey}]`} />}
                  placeholder='Type to search'
                  value={search}
                  onChange={onSearchValueChange}
                  sx={{ padding: '0 7px 0 16px' }}
                />
              </div>
              {!!displayedProducts?.length &&
                displayedProducts.map((product: IProduct) => (
                  <Box
                    key={product.id}
                    component='button'
                    type='button'
                    className={classNames(
                      'w-full text-start px-3 py-1 rounded-md font-[Manrope] text-[16px] text-[#303030] focus:outline-none',
                      {
                        'bg-[#1b43321f]': watchProduct?.id === product.id,
                        'cursor-not-allowed': isProductSelectionDisabled,
                      },
                    )}
                    onClick={handleProductSelection(product)}
                    disabled={isProductSelectionDisabled}
                    aria-label={product.name}
                  >
                    <span className='flex items-center justify-between gap-3'>
                      <span className='truncate'>{product.name}</span>
                      <span className={`shrink-0 text-[13px] text-[${Color.Grey}]`}>
                        {getProductInventoryHint(product)}
                      </span>
                    </span>
                  </Box>
                ))}
            </Box>
            <Box className='flex flex-col w-1/2' ref={publishersListRef}>
              {showProductDomainExport && (
                <ProductDomainExport
                  domains={domains}
                  productName={watchProduct!.name}
                  isLoading={areDomainsLoading}
                  error={domainsError}
                />
              )}
              {showInventorySearch && (
                <ProductInventorySearch
                  label={showDomains ? 'domains' : 'publishers'}
                  total={activeInventoryCount}
                  search={inventorySearch}
                  onChange={onInventorySearchChange}
                  onClear={clearInventorySearch}
                  hasNoMatch={!displayedInventoryCount}
                  className='px-2 pt-2'
                />
              )}
              {showPublishers && (
                <Fragment>
                  {presetsMode ? (
                    <PublisherSection publishers={selectedPublishers} className='flex-1 overflow-y-auto p-2' />
                  ) : (
                    <PublisherList publishers={displayedPublishers} className='flex-1 overflow-y-auto p-2' />
                  )}
                </Fragment>
              )}
              {showDomains && <ProductDomainList domains={displayedDomains} className='flex-1 overflow-y-auto p-2' />}
            </Box>
          </div>
        </div>
      )}
      <SupportRequestModal
        title={presetsMode ? 'Presets' : 'Products'}
        isOpen={requestProductsModalOpen}
        handleClose={onRequestProductsClose}
      />
    </div>
  );
};

export default ProductAndPublishersSelection;
